import {
    getSessionCookie,
    hasValidSessionCookie,
    setSessionCookieHasExpired,
    getActiveCompanyId,
    setSessionCookie,
} from "Shared/util/user-session";
import {
    ActivateErpIntegrationInputResource,
    AnnualKycFollowUpAnswerResource,
    ApiInvoiceGroups,
    AppData,
    BankResource,
    BannerResource,
    BrandContextResource,
    ComplianceSummary,
    ConsentResource,
    ConsentStatusResource,
    CreateConsentInputResource,
    CustomerAnnualKycFollowUp,
    CustomerContext,
    DeleteErpIntegrationInputResource,
    EIdAuthenticationFiTupasResource,
    EIdAuthenticationPurpose,
    EIdAuthenticationSeBankIdResource,
    EIdAuthenticationStatusResource,
    ErpIntegrationInvoiceImportInputResource,
    ErpIntegrationInvoiceImportResource,
    ErpIntegrationInvoiceResource,
    ErpIntegrationInvoiceSummaryResource,
    ErpIntegrationProviderIdResource,
    ErpIntegrationResource,
    GenerateSsoLinkInputResource,
    InvoiceDetailResource,
    PaymentSummary,
    TokenRefreshResult,
    UserAgentResource,
    UserContext,
    WebApiError,
} from "./types";
import {
    PayoutInformation,
    PayoutInformationConsentStatus,
    SavePayoutInformationAccountRequest,
} from "./types/api";

/* -- START TOKEN RENEWAL -- */

/* -- END TOKEN RENEWAL -- */

/* -- START HANDLE RESPONSE -- */

const parseResponse = <T extends any>(response: Response): Promise<T> => {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
        return response.json().then((x) => x as T);
    }
    return response.text().then((x) => x as T);
};

const parseFileResponse = <T extends any>(response: Response): Promise<T> => {
    if (
        response.headers.get("content-type")?.indexOf("application/json") !== -1
    ) {
        return response.json().then((x) => x as T);
    }
    return response.arrayBuffer().then(
        (arrayBuffer) =>
            ({
                contentType: response.headers.get("content-type"),
                fileName: decodeURIComponent(
                    response.headers
                        .get("content-disposition")
                        ?.split(";")
                        .find((n: any) => n.includes("filename*="))
                        ?.replace("filename*=UTF-8''", "")
                        .trim() || "",
                ),
                content: arrayBuffer,
            } as T),
    );
};

type HandleResponseOptions = {
    skipErrorHandling?: boolean;
    expectFileResponse?: boolean;
};

const handleResponse = <T extends any>(
    response: Response,
    { skipErrorHandling, expectFileResponse }: HandleResponseOptions = {},
): Promise<T> => {
    if (
        (response.status >= 200 && response.status < 300) ||
        !!skipErrorHandling
    ) {
        const newToken = response.headers.get("new-token");
        if (newToken) {
            setSessionCookie(newToken);
        }
        if (expectFileResponse) {
            return parseFileResponse<T>(response);
        }
        return parseResponse<T>(response);
    }
    if (response.status === 401) {
        setSessionCookieHasExpired();
        window.location.reload();
    }

    return parseResponse<string>(response).then((error) => {
        const webApiError = new Error(response.statusText) as WebApiError;
        webApiError.message = response.statusText;
        webApiError.response = response;
        webApiError.data = error;
        throw webApiError;
    });
};

type SendRequestOptions = {
    isFormData?: boolean;
    signal?: AbortSignal;
};

const sendRequest = (
    url: string,
    method: string,
    headers: any,
    data = undefined,
    { isFormData, signal }: SendRequestOptions = {},
) =>
    fetch(url, {
        headers:
            !isFormData && data
                ? {
                      ...headers,
                      "Content-Type": "application/json",
                  }
                : headers,
        method,
        body: !isFormData && data ? JSON.stringify(data) : data,
        signal,
    });

type AuthorizedSendRequestOptions = HandleResponseOptions &
    SendRequestOptions & {
        bearerToken?: string;
    };

const authorizedSendRequest = <T extends any>(
    url: string,
    method: string,
    headers?: any,
    data?: any,
    {
        bearerToken,
        skipErrorHandling,
        isFormData,
        signal,
        expectFileResponse,
    }: AuthorizedSendRequestOptions = {},
) => {
    const extendedHeaders = {
        ...headers,
        Authorization: `Bearer ${bearerToken || getSessionCookie()}`,
        ActiveCompanyId: getActiveCompanyId(),
    };
    return sendRequest(url, method, extendedHeaders, data, {
        isFormData,
        signal,
    }).then((response) =>
        handleResponse<T>(response, { skipErrorHandling, expectFileResponse }),
    );
};

/* -- END HANDLE RESPONSE -- */

/* -- START HTTP METHODS -- */

/* -- Authorized -- */
const fetchFromApi = <T extends any>(url: string) =>
    authorizedSendRequest<T>(url, "GET");

type PostToApiOptions = {
    signal?: AbortSignal;
    skipErrorHandling?: boolean;
};

const postToApi = <T extends any>(
    url: string,
    data?: any,
    bearerToken?: string,
    { skipErrorHandling, signal }: PostToApiOptions = {},
) =>
    authorizedSendRequest<T>(url, "POST", {}, data, {
        bearerToken,
        skipErrorHandling,
        signal,
    });

const postFormDataToApi = (url: string, formData: FormData) =>
    authorizedSendRequest(url, "POST", {}, formData, { isFormData: true });

const putToApi = <T extends any>(url: string, data?: any) =>
    authorizedSendRequest<T>(url, "PUT", {}, data);

const fetchFileFromApi = (url: string, data: any) =>
    authorizedSendRequest(url, "POST", {}, data, { expectFileResponse: true });

export const tryTokenRefresh = () =>
    new Promise<TokenRefreshResult>((resolve) => {
        postToApi<string>("/api/auth/refresh")
            .then((token) => {
                resolve({ ok: true, token });
            })
            .catch(() => {
                resolve({ ok: false });
            });
    });

/* -- Custom requests -- */

const putUnauthorizedToApi = (url: string, data: any) =>
    sendRequest(url, "PUT", {}, data).then(handleResponse);

const postSpecialAuthorizedToApi = (url: string, token: string, data: any) =>
    sendRequest(url, "POST", { token }, data).then(handleResponse);

/* -- END HTTP METHODS -- */

/* -- START REST API FUNCS -- */
export const fetchBrandContext = () =>
    fetchFromApi<BrandContextResource>("/api/context/brand");
export const fetchCustomerContext = () =>
    fetchFromApi<CustomerContext>("/api/context/customer");

export const fetchAppContext = () => fetchFromApi<AppData>("/api/context/app");

export const fetchUserContext = () => {
    if (hasValidSessionCookie()) {
        return fetchFromApi<UserContext>("/api/context/user");
    }
    return new Promise<UserContext>((resolve) =>
        resolve(undefined as any as UserContext),
    );
};

export const fetchUserBySsn = (socialSecurityNumber: string) => 
    fetchFromApi<UserContext>(`/api/User/userBySsn/${socialSecurityNumber}`);

export const fetchEncryptedUserData = () =>
    fetchFromApi("/api/businessloans/encryptedUserData");

export const fetchCreditInvoices = () => fetchFromApi("/api/invoices/credit");
export const fetchDeductedInvoices = () =>
    fetchFromApi("/api/invoices/deducted");
export const fetchTodaysInvoiceSummary = () =>
    fetchFromApi("/api/invoices/todays-invoice-summary");
export const fetchInvoiceEvents = () => fetchFromApi("/api/invoices/events");
export const fetchPayedInvoices = () => fetchFromApi("/api/invoices/payed");
export const fetchReceivablesInvoices = () =>
    fetchFromApi("/api/invoices/receivables");
export const fetchPaymentSummary = () =>
    fetchFromApi<PaymentSummary>("/api/invoices/payment-summary");
export const fetchPayments = () => fetchFromApi("/api/invoices/payments");

export const fetchDebtors = () => fetchFromApi("/api/invoices/debtors");
export const fetchDebtor = (debtorId = 0) =>
    fetchFromApi<any>(`/api/invoices/debtors/${debtorId}`);

export const fetchDebtorCurrentLedger = (debtorId = 0) =>
    fetchFromApi(`/api/invoices/debtors/${debtorId}/current-ledger`);
export const fetchDebtorAveragePayments = (debtorId = 0) =>
    fetchFromApi(`/api/invoices/debtors/${debtorId}/average-payment`);

export const fetchLedger = () => fetchFromApi("/api/invoices/ledger");
export const fetchHistoricalLedger = () =>
    fetchFromApi("/api/invoices/historical-ledger");

export const fetchTransactions = () => fetchFromApi("/api/transactions");

export const fetchDepositions = () => fetchFromApi("/api/depositions");

export const fetchInvoiceDetails = (invoiceId: number) =>
    fetchFromApi<InvoiceDetailResource>(`/api/invoices/${invoiceId}`);

export const fetchInvoiceGroup = (id: number) =>
    fetchFromApi<ApiInvoiceGroups>(`/api/invoicegroups/${id}`);

export const fetchDepositionGroup = (id: any) =>
    fetchFromApi(`/api/depositiongroups/${id}`);

export const fetchPayoutSpecificationPdf = (id: any) =>
    fetchFromApi<any>(`/api/transactions/generate-pdf/${id}`);

export const searchInvoices = (terms: any) =>
    fetchFromApi(`/api/invoices/search?terms=${terms}`);

export const fetchInvoicePdf = (sequenceNumber: number) =>
    fetchFromApi(`/api/files/invoices/${sequenceNumber}`);

export const fetchPriceInformation = () =>
    fetchFromApi("/api/price/information");

export const fetchBanner = () => fetchFromApi<BannerResource>("/api/banner");

/* -- ErpIntegration -- */
export const activateErpIntegration = (
    input: ActivateErpIntegrationInputResource,
) => postToApi<ErpIntegrationResource>("/api/erpintegration/activate", input);
export const deleteErpIntegration = (
    input: DeleteErpIntegrationInputResource,
) => postToApi("/api/erpintegration/delete", input);
export const fetchErpIntegration = (
    providerId: ErpIntegrationProviderIdResource,
) => fetchFromApi<ErpIntegrationResource>(`/api/erpintegration/${providerId}`);
export const fetchErpIntegrationInvoices = (
    providerId: ErpIntegrationProviderIdResource,
) =>
    fetchFromApi<ErpIntegrationInvoiceResource[]>(
        `/api/erpintegration/${providerId}/invoices`,
    );
export const fetchErpIntegrationInvoicesSummary = (
    providerId: ErpIntegrationProviderIdResource,
) =>
    fetchFromApi<ErpIntegrationInvoiceSummaryResource>(
        `/api/erpintegration/${providerId}/invoices-summary`,
    );
export const generateSsoLink = (input: GenerateSsoLinkInputResource) =>
    postToApi<string>("/api/erpintegration/generate-sso-link", input);
export const importErpIntegrationInvoices = (
    input: ErpIntegrationInvoiceImportInputResource,
) =>
    postToApi<ErpIntegrationInvoiceImportResource>(
        "/api/erpintegration/import-invoices",
        input,
    );

export const sendEmailWithInvoices = (formData: FormData) =>
    postFormDataToApi("/api/frendsgeneral/send-mail", formData);

export const fetchMigrationInfoText = () =>
    fetchFromApi("/api/InfoText/migration");
export const fetchDatabaseTranslations = () =>
    fetchFromApi("/api/DBText/translations");

export const loginWithMigration = (username: string, password: string) =>
    postToApi("/api/auth/migration", { username, password });
export const loginWithCredentials = (
    username: string,
    password: string,
    organizationNumber?: string,
) =>
    postToApi("/api/auth/credentials", {
        username,
        password,
        organizationNumber,
    });
export const authenticateEIdSeBankId = (
    purpose: EIdAuthenticationPurpose,
    userAgent: UserAgentResource,
) =>
    postToApi<EIdAuthenticationSeBankIdResource>("/api/auth/eid/se-bankid", {
        purpose,
        userAgent,
    });
export const authenticateEIdFiTupas = (purpose: EIdAuthenticationPurpose) =>
    postToApi<EIdAuthenticationFiTupasResource>("/api/auth/eid/fi-tupas", {
        purpose,
    });
export const fetchEIdAuthenticationStatusSeBankId = (eIdToken: string) =>
    postToApi<EIdAuthenticationStatusResource>(
        "/api/auth/eid/se-bankid/status",
        null,
        eIdToken,
    );
export const fetchEIdAuthenticationStatusFiTupas = (
    token: string,
    scriveTransactionId: string,
) =>
    postToApi<EIdAuthenticationStatusResource>(
        `/api/auth/eid/fi-tupas/status/${scriveTransactionId}`,
        null,
        token,
    );
export const cancelSeBankIdAuthentication = (eIdToken: string) =>
    postToApi("/api/auth/eid/se-bankid/cancel", null, eIdToken);
export const finalizeLoginWithEId = (
    verificationToken: string,
    organizationNumber?: string,
) =>
    postToApi("/api/auth/eid/login", { organizationNumber }, verificationToken);

export const activate = (
    activationId: any,
    password: string,
    verifiedPassword: string,
) =>
    postToApi("/api/Activate/activate", {
        activationId,
        password,
        verifiedPassword,
    });
export const activateWithEId = (activationId: any, verificationToken: string) =>
    postToApi(
        "/api/Activate/activate/eid",
        { activationId },
        verificationToken,
    );

export const createFirstUser = (token: string, data: any, language: string) =>
    postSpecialAuthorizedToApi(
        `/api/User/createFirstUser/${language}`,
        token,
        data,
    );
export const createUser = (data: any, language: string) =>
    postToApi(`/api/User/createUser/${language}`, data);
export const updateUser = (data: any) => putToApi("/api/User/updateUser", data);
export const updateUserSsn = (token: string) =>
    putToApi("/api/User/update-user-ssn", { verificationToken: token });
export const removeUserFromUg = (userId: any) =>
    putToApi(`/api/User/removeUserFromUg?userid=${userId}`);
export const decryptUserData = (data: any) =>
    postToApi("/api/User/decryptUserData", { data });

export const migrateOrg = (username: string, password: string) =>
    postToApi("/api/User/createUserFromCredentials", { username, password });
export const updateDefaultOrg = (ugId: any) =>
    putToApi(`/api/User/setUserDefaultCompany?companyId=${ugId}`);
export const orgUsers = (ugId: any) =>
    fetchFromApi(`/api/User/usersByUg/${ugId}`);
export const switchOrg = (ugId: any) =>
    postToApi(`/api/User/switchUg?ugId=${ugId}`);

export const forgotPassword = (email: string, language: string) =>
    putUnauthorizedToApi(`/api/User/forgotPassword/${language}`, { email });
export const updatePassword = (
    guid: string,
    password: string,
    verifiedPassword: string,
) =>
    putUnauthorizedToApi("/api/User/resetPassword", {
        guid,
        password,
        verifiedPassword,
    });
export const updatePasswordLoggedIn = (
    password: string,
    verifiedPassword: string,
    oldPassword: string,
) =>
    putToApi("/api/User/updatePassword", {
        password,
        verifiedPassword,
        oldPassword,
    });
export const checkAccountPlan = () =>
    fetchFromApi("/api/accountplan/checkAccountPlan");
export const getAccountPlan = () =>
    fetchFromApi("/api/accountplan/getAccountPlan");
export const saveAccountPlan = (accountPlan: any) =>
    postToApi("/api/accountplan/save", accountPlan);

/* -- Export -- */
export const exportTransactions = (exportQueryParams: any) =>
    fetchFileFromApi("/api/files/export-transactions", exportQueryParams);
export const exportPageTransactions = (exportQueryParams: any) =>
    fetchFileFromApi("/api/files/export-page-transactions", exportQueryParams);
export const exportPageTransactionsSummary = (exportQueryParams: any) =>
    fetchFileFromApi(
        "/api/files/export-page-transactions-summary",
        exportQueryParams,
    );
export const exportLedgerInvoices = (exportQueryParams: any) =>
    fetchFileFromApi("/api/files/invoices/export-ledger", exportQueryParams);
export const exportPaidInvoices = (exportQueryParams: any) =>
    fetchFileFromApi("/api/files/invoices/export-paid", exportQueryParams);
export const exportCreditInvoices = (exportQueryParams: any) =>
    fetchFileFromApi("/api/files/invoices/export-credit", exportQueryParams);
export const exportDeductedInvoices = (exportQueryParams: any) =>
    fetchFileFromApi("/api/files/invoices/export-deducted", exportQueryParams);
export const exportHistoricalLedger = (exportQueryParams: any) =>
    fetchFileFromApi(
        "/api/files/invoices/export-historical-ledger",
        exportQueryParams,
    );
export const exportReceivables = (exportQueryParams: any) =>
    fetchFileFromApi(
        "/api/files/invoices/export-receivables",
        exportQueryParams,
    );
export const exportCustomers = (exportQueryParams: any) =>
    fetchFileFromApi("/api/files/export-debtors", exportQueryParams);
export const exportDepositions = (exportQueryParams: any) =>
    fetchFileFromApi("/api/files/export-depositions", exportQueryParams);
export const exportPayments = (exportQueryParams: any) =>
    fetchFileFromApi("/api/files/export-payments", exportQueryParams);

/* -- Export Settings -- */
export const getExportSettings = () =>
    fetchFromApi("/api/ExportSettings/getExportOptions");

/* -- Compliance -- */
export const fetchComplianceSummary = () =>
    fetchFromApi<ComplianceSummary>("/api/compliance/summary");
export const fetchKycQuestions = () => fetchFromApi("/api/compliance/kyc");
export const saveKycAnswers = (answers: any) =>
    postToApi("/api/compliance/kyc", answers);
export const saveAnnualKycFollowUpAnswers = (
    data: AnnualKycFollowUpAnswerResource,
) =>
    postToApi<CustomerAnnualKycFollowUp>(
        "/api/compliance/annual-kyc-follow-up-answer",
        data,
    );

/* -- PaymentInformation -- */
export const getPayoutInformation = () =>
    fetchFromApi<PayoutInformation>(
        "/api/paymentinformation/payout-information",
    );

export const getPayoutInformationConsentStatus = (consentId: number) =>
    fetchFromApi<PayoutInformationConsentStatus>(
        `/api/paymentinformation/payout-information/consent/${consentId}/status`,
    );

export const listPayoutInformationCurrencies = () =>
    fetchFromApi<string[]>(
        "/api/paymentinformation/payout-information/currencies",
    );

export const savePayoutInformationAccount = (
    request: SavePayoutInformationAccountRequest,
) =>
    putToApi<PayoutInformation>(
        "/api/paymentinformation/payout-information/account",
        request,
    );

export const listOpenBankingBanks = () =>
    fetchFromApi<BankResource[]>("/api/paymentinformation/open-banking/banks");

export const createOpenBankingConsent = (
    createConsentInputResource: CreateConsentInputResource,
) =>
    postToApi<ConsentResource>(
        "/api/paymentinformation/open-banking/consent",
        createConsentInputResource,
    );

export const getOpenBankingConsentStatus = (
    token: string,
    userAgent: UserAgentResource,
) =>
    postToApi<ConsentStatusResource>(
        "/api/paymentinformation/open-banking/consent/status",
        userAgent,
        token,
    );

export const activateOpenBankingRedirectConsent = (
    activationCode: string,
    token: string,
) =>
    postToApi<void>(
        "/api/paymentinformation/open-banking/consent/activate",
        activationCode,
        token,
    );
